import { useCallback, useEffect, useMemo, useState } from 'react'
import type { WorkspaceRole } from '../../types/workspace'
import {
  resolveTabBar,
  type LockedTabItem,
  type TabBarPolicy,
  type WorkspaceTabDefaults,
} from './tabBarEngine'
import {
  saveTabPrefs,
  subscribeTabPrefs,
  subscribeWorkspaceTabDefaults,
  type TabPrefs,
} from './tabPrefsService'
import type { WorkspaceModuleSource } from '../../utils/workspaceModules'
import type { HelperTabKey, TabKey } from './tabCatalog'
import { subscribeToMyEffectiveControls } from '../memberControlsService'
import type { MemberControls } from '../../types/memberControls'
import { getHelperSnapshot, subscribeHelperStore } from '../helper/helperStore'

export interface UseHelperTabBarOptions {
  uid?: string
  workspaceId?: string
  role?: WorkspaceRole
  workspace?: WorkspaceModuleSource
  policy?: TabBarPolicy
  useCase?: string
  unreadByTab?: Partial<Record<HelperTabKey, boolean | number>>
  personal?: HelperTabKey[]
  effectiveControls?: MemberControls | null
  personHiddenPages?: TabKey[]
}

export interface UseHelperTabBarResult {
  pinned: HelperTabKey[]
  more: HelperTabKey[]
  locked: LockedTabItem[]
  moreHasUnread: boolean
  isTabUnread: (key: HelperTabKey) => boolean
  savePersonalTabs: (tabs: HelperTabKey[]) => Promise<void>
  isLoaded: boolean
}

/**
 * Hook providing reactive tab resolution for the Helper app bottom bar.
 * Subscribes to personal tabPrefs and workspace tabDefaults, resolves 4-pinned + More,
 * and tracks unread states with More roll-up (TB-P3, TB-D8).
 */
export function useHelperTabBar(options: UseHelperTabBarOptions = {}): UseHelperTabBarResult {
  const {
    uid,
    workspaceId,
    role = 'HELPER',
    workspace,
    policy,
    useCase,
    unreadByTab,
    personal: overridePersonal,
  } = options

  const [tabPrefs, setTabPrefs] = useState<TabPrefs | null>(null)
  const [tabDefaults, setTabDefaults] = useState<WorkspaceTabDefaults | null>(null)
  const [subscribedControls, setSubscribedControls] = useState<MemberControls | null>(null)
  const controls = options.effectiveControls !== undefined ? options.effectiveControls : subscribedControls
  const [isLoaded, setIsLoaded] = useState(!uid || !workspaceId)

  useEffect(() => {
    if (options.effectiveControls !== undefined || !workspaceId) {
      return
    }
    return subscribeToMyEffectiveControls(
      workspaceId,
      (loadedControls) => {
        setSubscribedControls(loadedControls)
      },
      uid,
    )
  }, [workspaceId, uid, options.effectiveControls])

  useEffect(() => {
    if (!uid || !workspaceId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets loaded status when no id
      setIsLoaded(true)
      return
    }

    let prefsLoaded = false
    let defaultsLoaded = false
    const checkLoaded = () => {
      if (prefsLoaded && defaultsLoaded) setIsLoaded(true)
    }

    const unsubPrefs = subscribeTabPrefs(uid, workspaceId, (loaded) => {
      setTabPrefs(loaded)
      prefsLoaded = true
      checkLoaded()
    })

    const unsubDefaults = subscribeWorkspaceTabDefaults(workspaceId, (defaults) => {
      setTabDefaults(defaults)
      defaultsLoaded = true
      checkLoaded()
    })

    return () => {
      unsubPrefs()
      unsubDefaults()
    }
  }, [uid, workspaceId])

  const effectivePersonal = overridePersonal ?? (tabPrefs?.helper as HelperTabKey[] | undefined)

  const [eduJobs, setEduJobs] = useState<string[]>(() => {
    const snap = typeof getHelperSnapshot === 'function' ? getHelperSnapshot() : null
    if (!snap) return []
    return workspaceId ? snap.eduJobsByWorkspace?.[workspaceId] ?? [] : snap.eduJobs ?? []
  })
  useEffect(() => {
    if (typeof subscribeHelperStore !== 'function') return
    return subscribeHelperStore((snap) => {
      if (!snap) return
      const jobs = workspaceId ? snap.eduJobsByWorkspace?.[workspaceId] ?? [] : snap.eduJobs ?? []
      setEduJobs(jobs)
    })
  }, [workspaceId])

  const hiddenPages = useMemo(() => {
    if (options.personHiddenPages) return options.personHiddenPages
    if (Array.isArray(controls?.tiles)) return controls.tiles as TabKey[]
    return undefined
  }, [options.personHiddenPages, controls?.tiles])

  const resolved = useMemo(() => {
    const effectivePolicy: TabBarPolicy = {
      ...policy,
      eduJobs: policy?.eduJobs ?? eduJobs,
    }
    return resolveTabBar({
      surface: 'helper',
      role,
      workspace,
      policy: effectivePolicy,
      workspaceDefaults: tabDefaults ?? undefined,
      personal: effectivePersonal,
      useCase,
      personHiddenPages: hiddenPages,
    })
  }, [effectivePersonal, workspace, policy, eduJobs, role, tabDefaults, useCase, hiddenPages])

  const pinned = resolved.pinned as HelperTabKey[]
  const more = resolved.more as HelperTabKey[]

  const isTabUnread = useCallback(
    (key: HelperTabKey) => {
      const val = unreadByTab?.[key]
      if (typeof val === 'number') return val > 0
      return Boolean(val)
    },
    [unreadByTab],
  )

  const moreHasUnread = useMemo(() => {
    return more.some((key) => isTabUnread(key))
  }, [isTabUnread, more])

  const savePersonalTabs = useCallback(
    async (tabs: HelperTabKey[]) => {
      setTabPrefs((prev) => ({
        ...prev,
        helper: tabs,
      }))
      if (!uid || !workspaceId) return
      try {
        await saveTabPrefs(uid, workspaceId, { helper: tabs })
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[useHelperTabBar] Remote tab preferences save failed, keeping local layout:', err)
        }
      }
    },
    [uid, workspaceId],
  )

  return {
    pinned,
    more,
    locked: resolved.locked,
    moreHasUnread,
    isTabUnread,
    savePersonalTabs,
    isLoaded,
  }
}
